'use client'
import { Button, ButtonProps } from '@components/button'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import Client from 'shopify-buy'
import { getProductInventory, SHOPIFY_QUERY_RATE, ShopifyProduct, shopifyPublicAccessToken } from '@utils/shopify'
import { useQuery } from '@tanstack/react-query'
import React from 'react'

export interface ShopifyPurchaseButtonProps extends ButtonProps {
  shopifyProduct: ShopifyProduct
  features?: Record<string, string>
  buttonTextOverride?: string
}

const ShopifyPurchaseButton = React.forwardRef<HTMLButtonElement, ShopifyPurchaseButtonProps>(
  ({ shopifyProduct, features, buttonTextOverride, ...props }: ShopifyPurchaseButtonProps, ref) => {
    // Initializing a client to return content in the store's primary language
    const client = Client.buildClient({
      domain: `shop.artblocks.io`,
      storefrontAccessToken: shopifyPublicAccessToken,
      apiVersion: '2024-04'
    })

    const { data: inventory, isFetching } = useQuery({
      queryKey: ['inventory', shopifyProduct.productId],
      queryFn: ({ queryKey }) => getProductInventory(queryKey[1], false),
      refetchInterval: (query) =>
        query.state.data === undefined || query.state.data > 0 ? SHOPIFY_QUERY_RATE * 1000 : false
    })

    const soldOut = inventory !== undefined && inventory <= 0
    const handleCheckout = async () => {
      const checkout = await client.checkout.create()
      const lineItemsToAdd: Client.CheckoutLineItemInput[] = [
        {
          variantId: shopifyProduct.variantId,
          quantity: 1,
          // The line item property
          customAttributes: features ? Object.entries(features).map(([key, value]) => ({ key, value })) : []
        }
      ]

      // Add an item to the checkout
      await client.checkout.addLineItems(checkout.id, lineItemsToAdd)

      // Redirect to the checkout
      window.location.href = checkout.webUrl
    }

    let buttonText = buttonTextOverride ?? 'PURCHASE'

    if (soldOut) {
      buttonText = 'SOLD OUT'
    } else if (isFetching) {
      buttonText = 'LOADING...'
    }

    return (
      <Button
        ref={ref}
        onClick={() => {
          trackButtonClicked(TrackedButtonNames.mpkozCreatorPurchase, {})
          handleCheckout()
        }}
        disabled={soldOut || isFetching}
        {...props}
      >
        {buttonText}
      </Button>
    )
  }
)

ShopifyPurchaseButton.displayName = 'ShopifyPurchaseButton'

export { ShopifyPurchaseButton }
